import { z } from 'zod';
/**
 * Recurring intraday markers, defined on the instrument's STORED clock.
 *
 * Two shapes:
 *   TimeWindow — a span (03:00–04:00), drawn as a shaded band.
 *   TimePoint  — a single moment (03:00), drawn as a dashed vertical line.
 *
 * Stored-clock minutes, not UTC and not the viewer's local time. The stored
 * clock is what every candle timestamp is already on and what the chart axis
 * shows, so a marker defined here lines up with the bars without conversion.
 *
 * Note the exporter applies a FIXED +3h to a UTC broker feed. Because it is
 * fixed, a stored-clock time maps to the same UTC instant all year — 03:00
 * stored is always 00:00 UTC. That is what makes markers anchored to non-DST
 * zones (Tokyo) exact, and equally why one pinned to a zone that DOES shift
 * (London, New York) drifts by an hour twice a year and needs a seasonal
 * variant.
 */
export const hm = (hours, minutes = 0) => hours * 60 + minutes;
export const IMPORTANCE_LEVELS = [1, 2, 3];
export const IMPORTANCE_LABELS = {
    1: 'Medium',
    2: 'High',
    3: 'Very high',
};
/** "!", "!!" or "!!!" */
export function importanceMarks(level) {
    return '!'.repeat(level);
}
/** Band fill opacity as a two-digit hex suffix, keyed by importance. */
export const IMPORTANCE_ALPHA = {
    1: '14',
    2: '24',
    3: '3d',
};
const hexColor = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Colour must be a hex value like #38bdf8');
export const timeWindowSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    startMinute: z.number().int().min(0).max(1439),
    endMinute: z.number().int().min(0).max(1440),
    color: z.string(),
    description: z.string(),
    enabled: z.boolean(),
    importance: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    timezone: z.string().nullable(),
    weekdays: z.array(z.number().int().min(0).max(6)).optional(),
    instrumentIds: z.array(z.number().int()),
});
/** Fields a client may set. `id` is assigned by the database. */
export const timeWindowInputSchema = z
    .object({
    name: z.string().trim().min(1).max(64),
    startMinute: z.number().int().min(0).max(1439),
    endMinute: z.number().int().min(0).max(1440),
    color: hexColor,
    description: z.string().max(255).default(''),
    enabled: z.boolean().default(true),
    importance: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
    timezone: z.string().nullable().default(null),
    weekdays: z.array(z.number().int().min(0).max(6)).max(7).optional(),
    instrumentIds: z.array(z.number().int()).default([]),
})
    // A window with identical bounds spans nothing and would silently never
    // match a bar, which reads as "the feature is broken" rather than "the
    // window is empty".
    .refine((w) => w.startMinute !== w.endMinute, {
    message: 'Start and end cannot be the same time.',
    path: ['endMinute'],
});
export const timePointSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    minute: z.number().int().min(0).max(1439),
    color: z.string(),
    description: z.string(),
    enabled: z.boolean(),
    timezone: z.string().nullable(),
    weekdays: z.array(z.number().int().min(0).max(6)).optional(),
    instrumentIds: z.array(z.number().int()),
});
export const timePointInputSchema = z.object({
    name: z.string().trim().min(1).max(64),
    minute: z.number().int().min(0).max(1439),
    color: hexColor,
    description: z.string().max(255).default(''),
    enabled: z.boolean().default(true),
    timezone: z.string().nullable().default(null),
    weekdays: z.array(z.number().int().min(0).max(6)).max(7).optional(),
    instrumentIds: z.array(z.number().int()).default([]),
});
// ---------------------------------------------------------------- seeds
/**
 * Seeds, inserted once by the migration that creates the tables.
 *
 * Defaults, not the live configuration — both are edited in the settings panel
 * and stored in the database. These are only ever read on a fresh install.
 */
export const DEFAULT_TIME_WINDOWS = [
    {
        name: 'Tokyo Pre-Open',
        startMinute: hm(8, 30),
        endMinute: hm(9),
        timezone: 'Asia/Tokyo',
        color: '#38bdf8',
        description: 'Final 30 minutes of the Tokyo pre-open auction, before the 09:00 bell.',
        enabled: true,
        // Volatile but not structural: across 1093 trading days the day's high or
        // low forms here slightly LESS often than its length alone would predict.
        importance: 2,
        instrumentIds: [],
    },
    {
        name: 'Asia Session',
        startMinute: hm(9),
        endMinute: hm(13),
        timezone: 'Asia/Tokyo',
        color: '#0ea5e9',
        description: 'Tokyo cash morning. Hong Kong and Singapore open an hour in, Shanghai 30 minutes after that.',
        enabled: true,
        importance: 2,
        instrumentIds: [],
    },
    {
        name: 'Tokyo Close Session',
        startMinute: hm(13),
        endMinute: hm(16),
        timezone: 'Asia/Tokyo',
        color: '#6366f1',
        description: 'Tokyo afternoon through the 15:00 close, then the thin hour before Europe.',
        enabled: true,
        importance: 2,
        instrumentIds: [],
    },
    {
        name: 'London Open',
        startMinute: hm(8),
        endMinute: hm(9),
        timezone: 'Europe/London',
        color: '#a855f7',
        description: 'The opening hour of London. Europe\u2019s liquidity arrives and the Asian range is usually swept here.',
        enabled: true,
        importance: 3,
        instrumentIds: [],
    },
    {
        /*
         * Split out of London Open, which used to run 08:00-11:00.
         *
         * As one three-hour block it averaged the opening sweep together with two
         * hours of mid-morning drift, and the combination scored below chance as a
         * turning-point window despite being marked very high. Separating them lets
         * the hour that does the work keep the high importance and the continuation
         * carry its own, lower weight. The LBMA AM auction at 10:30 falls inside
         * this half.
         */
        name: 'London Morning',
        startMinute: hm(9),
        endMinute: hm(11),
        timezone: 'Europe/London',
        color: '#8b5cf6',
        description: 'London continuation after the opening hour, through the 10:30 LBMA AM auction.',
        enabled: true,
        importance: 2,
        instrumentIds: [],
    },
    {
        name: 'London Midday',
        startMinute: hm(11),
        endMinute: hm(13, 30),
        timezone: 'Europe/London',
        color: '#c084fc',
        description: 'Midday lull before the US session. Volume thins and ranges compress.',
        enabled: true,
        importance: 1,
        instrumentIds: [],
    },
    {
        name: 'US Data Window',
        startMinute: hm(8, 30),
        endMinute: hm(9, 30),
        timezone: 'America/New_York',
        color: '#f59e0b',
        description: 'CPI, NFP, PPI and most US macro releases land at 08:30 ET, an hour before the equity bell.',
        enabled: true,
        importance: 3,
        instrumentIds: [],
    },
    {
        name: 'New York Open',
        startMinute: hm(9, 30),
        endMinute: hm(11),
        timezone: 'America/New_York',
        color: '#fb923c',
        description: 'NYSE bell through the London\u2013New York overlap. Deepest liquidity of the day.',
        enabled: true,
        importance: 3,
        instrumentIds: [],
    },
    {
        name: 'London Close',
        startMinute: hm(16),
        endMinute: hm(19),
        timezone: 'Europe/London',
        color: '#f472b6',
        description: 'London equities close at 16:30 while New York runs on. European flow exits and moves can extend.',
        enabled: true,
        importance: 2,
        instrumentIds: [],
    },
    {
        name: 'US Afternoon',
        startMinute: hm(14),
        endMinute: hm(16),
        timezone: 'America/New_York',
        color: '#94a3b8',
        description: 'New York afternoon into the 16:00 equity close. Liquidity drains as Europe has gone.',
        enabled: true,
        importance: 2,
        instrumentIds: [],
    },
];
export const DEFAULT_TIME_POINTS = [
    {
        name: 'Tokyo Open',
        minute: hm(9),
        timezone: 'Asia/Tokyo',
        color: '#38bdf8',
        description: 'Tokyo Stock Exchange bell, 09:00 JST.',
        enabled: true,
        instrumentIds: [],
    },
    {
        name: 'Hong Kong Open',
        minute: hm(9),
        timezone: 'Asia/Hong_Kong',
        color: '#0ea5e9',
        description: 'Hong Kong and Singapore open. Shanghai follows 30 minutes later.',
        enabled: true,
        instrumentIds: [],
    },
    {
        name: 'Shanghai Open',
        minute: hm(9, 30),
        timezone: 'Asia/Shanghai',
        color: '#ef4444',
        description: 'Shanghai Stock Exchange open, 09:30 China time, 30 minutes after Hong Kong. China has no daylight saving, so this is 01:30 UTC all year.',
        enabled: true,
        instrumentIds: [],
    },
    {
        name: 'Europe Pre-Market',
        minute: hm(6),
        timezone: 'Europe/London',
        color: '#6366f1',
        description: 'European desks arrive two hours before the cash open; volume begins building.',
        enabled: true,
        instrumentIds: [],
    },
    {
        name: 'Tokyo Pre-Close',
        minute: hm(14, 30),
        timezone: 'Asia/Tokyo',
        color: '#60a5fa',
        description: 'Final half hour of the Tokyo cash session, when positions are squared before the 15:00 close. 08:30 Istanbul year-round — neither zone uses DST.',
        enabled: true,
        instrumentIds: [],
    },
    {
        name: 'Tokyo Close',
        minute: hm(15),
        timezone: 'Asia/Tokyo',
        color: '#818cf8',
        description: 'Tokyo Stock Exchange close. Asian positioning is squared here.',
        enabled: true,
        instrumentIds: [],
    },
    {
        name: 'London Open',
        minute: hm(8),
        timezone: 'Europe/London',
        color: '#a855f7',
        description: 'London cash open. The largest single injection of FX and gold liquidity.',
        enabled: true,
        instrumentIds: [],
    },
    {
        name: 'LBMA AM Fix',
        minute: hm(10, 30),
        timezone: 'Europe/London',
        color: '#f9a8d4',
        description: 'LBMA Gold Price AM auction, 10:30 London. The morning half of the twice-daily benchmark that physical contracts settle against.',
        enabled: true,
        instrumentIds: [],
    },
    {
        name: 'COMEX Open',
        minute: hm(8, 20),
        timezone: 'America/New_York',
        color: '#f59e0b',
        description: 'COMEX gold futures regular session opens, 08:20 New York. Futures liquidity steps up ten minutes before the 08:30 data releases.',
        enabled: true,
        instrumentIds: [],
    },
    {
        name: 'US Session Open',
        minute: hm(8),
        timezone: 'America/New_York',
        color: '#fdba74',
        description: 'Start of US banking hours \u2014 the "New York session" open quoted in session tables. The NYSE bell follows 90 minutes later.',
        enabled: true,
        instrumentIds: [],
    },
    {
        name: 'New York Open',
        minute: hm(9, 30),
        timezone: 'America/New_York',
        color: '#fb923c',
        description: 'NYSE opening bell, inside the London\u2013New York overlap.',
        enabled: true,
        instrumentIds: [],
    },
    {
        name: 'LBMA PM Fix',
        // 15:00 London, the actual auction time. Anchored to the zone so it stays
        // on the auction in both BST and GMT.
        minute: hm(15),
        timezone: 'Europe/London',
        color: '#f472b6',
        description: 'LBMA Gold Price PM auction, 15:00 London \u2014 the global gold benchmark.',
        enabled: true,
        instrumentIds: [],
    },
    {
        name: 'COMEX Pre-Settlement',
        minute: hm(13),
        timezone: 'America/New_York',
        color: '#fbbf24',
        description: 'Half an hour before COMEX gold settles on the 13:29:30\u201313:30 window. Futures flow concentrates into it.',
        enabled: true,
        instrumentIds: [],
    },
    {
        name: 'New York Close',
        minute: hm(16),
        timezone: 'America/New_York',
        color: '#94a3b8',
        description: 'NYSE closing bell. US equity flow ends and liquidity thins sharply.',
        enabled: true,
        instrumentIds: [],
    },
];
// ---------------------------------------------------------------- zones
/**
 * Timezones a marker can be anchored to.
 *
 * `null` means the broker's own stored clock — the original behaviour, and
 * still right for a marker defined by the broker's day rather than by a city.
 */
export const MARKER_ZONES = [
    { id: null, label: 'Broker clock (as stored)' },
    { id: 'Europe/London', label: 'London' },
    { id: 'America/New_York', label: 'New York' },
    { id: 'Asia/Tokyo', label: 'Tokyo' },
    { id: 'Asia/Hong_Kong', label: 'Hong Kong' },
    { id: 'Asia/Shanghai', label: 'Shanghai' },
    { id: 'Australia/Sydney', label: 'Sydney' },
    { id: 'UTC', label: 'UTC' },
];
/**
 * Offset of a timezone from UTC, in seconds, at a given instant.
 *
 * Derived by formatting the instant in the zone and reading it back as if it
 * were UTC. This defers to the platform's IANA database rather than hardcoding
 * transition dates, which governments change — Britain, the EU and the US have
 * all legislated on abolishing DST in recent years.
 */
const offsetCache = new Map();
export function zoneOffsetSeconds(utcSeconds, timeZone) {
    /*
     * Cached per zone per UTC HOUR, not per day.
     *
     * DST transitions land on the hour, so hour granularity is exact while still
     * collapsing thousands of bars onto a handful of Intl calls. Caching per day
     * would be an hour wrong for every bar on a transition day — precisely the
     * case this whole mechanism exists to get right.
     */
    const hour = Math.floor(utcSeconds / 3600);
    const key = `${timeZone}:${hour}`;
    const hit = offsetCache.get(key);
    if (hit !== undefined)
        return hit;
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(new Date(utcSeconds * 1000));
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    const asIfUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second')) /
        1000;
    const offset = Math.round(asIfUtc - utcSeconds);
    offsetCache.set(key, offset);
    return offset;
}
/**
 * Minute of the day a bar falls on, in the marker's own frame.
 *
 * With no timezone this is the broker's stored clock, unchanged. With one, the
 * stored timestamp is converted back to a true instant and then into that
 * zone — which is what makes "15:00 London" mean 15:00 London in both January
 * and July, rather than drifting an hour with the seasons.
 */
export function markerMinuteOfDay(storedSeconds, storedGmtOffsetSec, timeZone) {
    if (!timeZone)
        return storedMinuteOfDay(storedSeconds);
    const utc = storedSeconds - storedGmtOffsetSec;
    const local = utc + zoneOffsetSeconds(utc, timeZone);
    return storedMinuteOfDay(local);
}
/**
 * The UTC minute-of-day a marker currently fires at.
 *
 * Used for ordering. Once markers live in different zones, sorting by their
 * local minute is meaningless — Tokyo 08:30 and London 08:00 are seven hours
 * apart but would sort as though London came first. Converting to a common
 * frame puts the list back in the order the trading day actually runs.
 *
 * Depends on the reference instant because the answer moves with DST, which is
 * why this is computed at read time rather than stored.
 */
export function markerUtcMinute(minute, timeZone, referenceUtcSeconds) {
    if (!timeZone)
        return minute;
    const offsetMinutes = Math.round(zoneOffsetSeconds(referenceUtcSeconds, timeZone) / 60);
    return ((minute - offsetMinutes) % 1440 + 1440) % 1440;
}
/**
 * Sort key placing a marker in trading-day order.
 *
 * Raw UTC minutes are not enough. The forex day opens at 17:00 New York — about
 * 21:00 UTC — so a Tokyo marker at 23:30 UTC is the FIRST session of the day but
 * sorts last on a midnight-based key. Rotating the key to the session open puts
 * Tokyo, London and New York in the order they actually trade.
 *
 * 21:00 is used as the rotation point rather than the exact DST-aware boundary
 * because this only orders a list; being an hour out in winter cannot reorder
 * anything, as no session sits within an hour of the rollover.
 */
const TRADING_DAY_START_MINUTE = 21 * 60;
export function markerSortKey(minute, timeZone, referenceUtcSeconds) {
    const utc = markerUtcMinute(minute, timeZone, referenceUtcSeconds);
    return (utc - TRADING_DAY_START_MINUTE + 1440) % 1440;
}
/** Weekday in the marker's own frame, 0=Sunday. */
export function markerWeekday(storedSeconds, storedGmtOffsetSec, timeZone) {
    if (!timeZone)
        return storedWeekday(storedSeconds);
    const utc = storedSeconds - storedGmtOffsetSec;
    return storedWeekday(utc + zoneOffsetSeconds(utc, timeZone));
}
// ---------------------------------------------------------------- helpers
/** Minutes past midnight on the stored clock for a stored timestamp. */
export function storedMinuteOfDay(storedSeconds) {
    const d = new Date(storedSeconds * 1000);
    // UTC getters: the number is a wall-clock reading on the shifted broker clock,
    // not a true instant, so local-time getters would report the wrong hour.
    return d.getUTCHours() * 60 + d.getUTCMinutes();
}
/** Weekday (0=Sunday) on the stored clock. */
export function storedWeekday(storedSeconds) {
    return new Date(storedSeconds * 1000).getUTCDay();
}
/**
 * Is this stored timestamp inside the window?
 *
 * `storedGmtOffsetSec` is required because a zone-anchored window has to be
 * resolved back to a true instant first — see markerMinuteOfDay. It is ignored
 * for windows on the broker clock.
 */
export function isInWindow(storedSeconds, w, storedGmtOffsetSec = 0) {
    if (w.weekdays && w.weekdays.length > 0) {
        // Weekday is evaluated in the marker's own frame too: near midnight a
        // London-anchored window can sit on a different calendar day from the
        // broker clock, and filtering on the wrong one drops real occurrences.
        if (!w.weekdays.includes(markerWeekday(storedSeconds, storedGmtOffsetSec, w.timezone))) {
            return false;
        }
    }
    const m = markerMinuteOfDay(storedSeconds, storedGmtOffsetSec, w.timezone);
    return w.startMinute <= w.endMinute
        ? m >= w.startMinute && m < w.endMinute
        : // Wraps midnight, e.g. 22:00-02:00.
            m >= w.startMinute || m < w.endMinute;
}
/** The instant a marker fires on the local day containing `anchorUtc`. */
function markerInstantNear(marker, anchorUtc, storedGmtOffsetSec) {
    const zone = marker.timezone;
    const offsetAt = (utc) => (zone ? zoneOffsetSeconds(utc, zone) : storedGmtOffsetSec);
    const offset = offsetAt(anchorUtc);
    const localDayStart = Math.floor((anchorUtc + offset) / 86400) * 86400;
    // Resolved twice: the offset in force at the anchor is not necessarily the one
    // in force at the target minute, and on a transition day using the former
    // would place the marker an hour out.
    const utc = localDayStart + marker.minute * 60 - offset;
    const settled = offsetAt(utc);
    return settled === offset ? utc : localDayStart + marker.minute * 60 - settled;
}
function markerWeekdayAllowed(marker, utc, storedGmtOffsetSec) {
    if (!marker.weekdays || marker.weekdays.length === 0)
        return true;
    const zone = marker.timezone;
    const offset = zone ? zoneOffsetSeconds(utc, zone) : storedGmtOffsetSec;
    return marker.weekdays.includes(storedWeekday(utc + offset));
}
// Eight days covers any weekday restriction, including one allowing a single
// day of the week.
const MARKER_SEARCH_DAYS = 8;
export function nextMarkerOccurrenceUtc(marker, fromUtcSeconds, storedGmtOffsetSec = 0) {
    for (let day = 0; day <= MARKER_SEARCH_DAYS; day++) {
        const utc = markerInstantNear(marker, fromUtcSeconds + day * 86400, storedGmtOffsetSec);
        if (utc <= fromUtcSeconds)
            continue;
        if (!markerWeekdayAllowed(marker, utc, storedGmtOffsetSec))
            continue;
        return utc;
    }
    return null;
}
/**
 * The most recent instant a marker fired, at or before `fromUtcSeconds`.
 *
 * Pairs with `nextMarkerOccurrenceUtc` to bound the current wait, which is what
 * lets a progress bar know how far through it is. Without a start point the only
 * options are a fixed horizon — empty and uninformative whenever the next marker
 * is further out than the horizon — or nothing at all.
 */
export function previousMarkerOccurrenceUtc(marker, fromUtcSeconds, storedGmtOffsetSec = 0) {
    for (let day = 0; day <= MARKER_SEARCH_DAYS; day++) {
        const utc = markerInstantNear(marker, fromUtcSeconds - day * 86400, storedGmtOffsetSec);
        if (utc > fromUtcSeconds)
            continue;
        if (!markerWeekdayAllowed(marker, utc, storedGmtOffsetSec))
            continue;
        return utc;
    }
    return null;
}
/**
 * Does this bar carry the point?
 *
 * True when the point falls anywhere inside the bar's own duration, not only
 * when it equals the bar's open time. Without that, a point at 03:05 would
 * simply never appear on an H1 chart whose bars open on the hour.
 */
export function barCarriesPoint(storedSeconds, barSeconds, p, storedGmtOffsetSec = 0) {
    if (p.weekdays && p.weekdays.length > 0) {
        if (!p.weekdays.includes(markerWeekday(storedSeconds, storedGmtOffsetSec, p.timezone))) {
            return false;
        }
    }
    const start = markerMinuteOfDay(storedSeconds, storedGmtOffsetSec, p.timezone);
    const spanMinutes = Math.max(1, Math.round(barSeconds / 60));
    const offset = (p.minute - start + 1440) % 1440;
    return offset < spanMinutes;
}
export function formatMinute(minute) {
    const h = Math.floor(minute / 60) % 24;
    const m = minute % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
/** Parse "HH:MM" into minutes past midnight, or null. */
export function parseMinute(value) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
    if (!match)
        return null;
    const h = Number(match[1]);
    const m = Number(match[2]);
    if (h > 23 || m > 59)
        return null;
    return h * 60 + m;
}
export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const windowStatsSchema = z.object({
    windowId: z.number().int(),
    occurrences: z.number().int(),
    up: z.number().int(),
    down: z.number().int(),
    upRate: z.number().nullable(),
    avgRange: z.number(),
    avgAbsChange: z.number(),
    avgChange: z.number(),
});
/** Bars scanned per timeframe when computing window statistics. */
export const WINDOW_SCAN_BARS = {
    M1: 20000,
    M5: 20000,
    M15: 20000,
    M30: 20000,
    H1: 20000,
    H4: 8000,
    H4_1: 8000,
    H4_2: 8000,
    H4_3: 8000,
    D1: 3000,
    W1: 900,
};
/**
 * How many bars the Time Windows chart is given to scroll through.
 *
 * Per timeframe rather than one flat number, because a bar count means a wildly
 * different span depending on the timeframe: 1500 bars is five days of M5 but
 * ten months of D1. The figures below are chosen as spans — roughly a year of
 * H1, a week of M1 — so scrolling back reaches something useful on every tab
 * instead of running out after a few days on the slower ones.
 *
 * Always at most WINDOW_SCAN_BARS for the same timeframe: the chart is the tail
 * of the series the statistics were computed over, so every band drawn is backed
 * by bars that were also counted.
 */
export const WINDOW_CHART_BARS = {
    // M1 is capped low on purpose. A year of it is ~370k bars, and even the
    // fraction that fits is unreadable long before you finish scrolling it.
    M1: 2000,
    M5: 3000,
    M15: 4000,
    M30: 5000,
    // ~115 H1 bars a week, so this is a little over a year of trading hours.
    H1: 6000,
    H4: 3000,
    H4_1: 3000,
    H4_2: 3000,
    H4_3: 3000,
    D1: 3000,
    W1: 900,
};
/** Does a marker apply to this instrument? An empty list means every pair. */
export function appliesToInstrument(marker, instrumentId) {
    return marker.instrumentIds.length === 0 || marker.instrumentIds.includes(instrumentId);
}
//# sourceMappingURL=timeWindow.js.map